// TV runtime core: server connection, screen state, channels, mode
// (gallery/launcher), Edge requests and loading widget bundles.
import { DEFAULT_LOCALE, isLocale, type BundleMap, type Locale, type ScreenDoc, type ServerMessage, type TvMessage } from '@frame/protocol';
import type { Mode, OpenAppOptions, RuntimeApi } from '@frame/sdk/tv';
import { callTvApi } from './tvapi.ts';
import { Remote } from './remote.ts';
import { playAudio, preloadAudio, releaseAudio } from './audio.ts';
import { messages } from './i18n.ts';

declare const tizen: any;
declare const webapis: any;

export const RUNTIME_VERSION = '2.0.0';
const params = new URLSearchParams(location.search);
export const IS_TV = typeof (window as any).tizen !== 'undefined';
export const PREVIEW = !IS_TV && (params.has('preview') || params.has('mode'));
const FORCED_MODE = params.get('mode') as Mode | null;

type Listener = () => void;

// Verified store IDs (the TV's 127.0.0.1:8001 service reports "visible" for these).
const KNOWN_STORE_IDS: Record<string, string> = {
  '9Ur5IzDKqV.TizenYouTube': '111299001912',
  'org.tizen.netflix-app': '3201907018807',
  'org.tizen.primevideo': '3201910019365',
  'MCmYXNxgcu.DisneyPlus': '3201901017640',
  'rJeHak5zRg.Spotify': '3201606009684',
  '5b8c3eb16b.BeamCTVDev': '3202301029760',
  'com.samsung.tv.aria-video': '3201807016597',
  'org.tizen.browser': '3202010022079',
};
const TV_APPS_API = 'http://127.0.0.1:8001/api/v2/applications/';
type Widgets = Record<string, preact.ComponentType<any>>;

function store(key: string, value?: string | null) {
  try {
    if (value === undefined) return localStorage.getItem(key);
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {}
  return null;
}

/** Last known language: used before the first screen arrives (e.g. the approval overlay). */
function storedLocale(): Locale {
  const l = store('fl.locale');
  return isLocale(l) ? l : DEFAULT_LOCALE;
}

function deviceInfo() {
  let id = '';
  let model = '';
  try {
    id = webapis.productinfo.getDuid();
    model = webapis.productinfo.getRealModel();
  } catch {}
  if (!id) {
    id = store('fl.deviceId') || `web-${Math.random().toString(36).slice(2, 10)}`;
    store('fl.deviceId', id);
  }
  const t = messages.get(storedLocale());
  return { id, model, name: PREVIEW ? t.previewName : model ? `Samsung ${model}` : t.browserName };
}

export class Runtime implements RuntimeApi {
  readonly isTV = IS_TV;
  readonly preview = PREVIEW;
  readonly baseUrl: string;

  // --- state (the App component subscribes) ---
  doc: ScreenDoc | null = null;
  bundles: BundleMap = {};
  widgets = new Map<string, Widgets>(); // module → components
  status: 'connecting' | 'online' | 'offline' = 'connecting';
  approved = true;
  deviceId = '';
  toastText = '';

  private listeners = new Set<Listener>();
  private channels = new Map<string, unknown>();
  private channelSubs = new Map<string, Set<(v: unknown) => void>>();
  private shared = new Map<string, unknown>();
  private sharedSubs = new Map<string, Set<(v: unknown) => void>>();
  private modeValue: Mode = 'idle';
  private modeSubs = new Set<(m: Mode) => void>();
  private localeValue: Locale = storedLocale();
  private localeSubs = new Set<(l: Locale) => void>();
  private commandSubs = new Map<string, Set<(d?: unknown) => void>>();
  private backHandlers: (() => boolean)[] = [];
  private awakeHolds = 0;
  /** Set by the App component: renders a zone. */
  zoneRenderer: (name: string) => preact.VNode | null = () => null;
  private pending = new Map<string, { res: (v: unknown) => void; rej: (e: Error) => void }>();
  private ws?: WebSocket;
  private outbox: TvMessage[] = [];
  private seq = 0;
  private idleTimer?: ReturnType<typeof setTimeout>;
  private toastTimer?: ReturnType<typeof setTimeout>;
  private loadingBundles = new Map<string, Promise<void>>();
  private device = deviceInfo();
  /** Phone remote control (frame.remote.* Edge calls). */
  readonly remote = new Remote(this);

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.deviceId = this.device.id;
    // Draw the last screen from the cache right away: the launcher shows as soon as the TV turns on.
    try {
      const cached = JSON.parse(store(this.cacheKey()) || 'null');
      if (cached?.doc) {
        this.doc = cached.doc;
        this.bundles = cached.bundles;
        if (isLocale(cached.doc.locale)) this.localeValue = cached.doc.locale;
        for (const [k, v] of Object.entries(cached.channels || {})) this.channels.set(k, v);
        this.loadBundles();
      }
    } catch {}
  }

  private cacheKey() {
    return `fl.cache.${this.baseUrl}`;
  }

  // ---------- subscriptions ----------
  subscribeState(fn: Listener) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  private emit() {
    this.listeners.forEach((l) => l());
  }

  // ---------- connection ----------
  connect() {
    const url = this.baseUrl.replace(/^http/, 'ws') + 'ws';
    const ws = new WebSocket(url);
    this.ws = ws;
    ws.onopen = () => {
      this.status = 'online';
      this.send({
        t: 'hello',
        deviceId: this.device.id,
        name: this.device.name,
        model: this.device.model,
        token: store(`fl.token.${this.baseUrl}`) || undefined,
        runtime: RUNTIME_VERSION,
        role: PREVIEW || !IS_TV ? 'preview' : 'tv',
        draft: params.get('draft') === '1',
      });
      const queued = this.outbox.splice(0);
      queued.forEach((m) => this.send(m));
      this.emit();
    };
    ws.onmessage = (e) => this.onMessage(JSON.parse(e.data));
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.status = 'offline';
      this.emit();
      setTimeout(() => this.connect(), 3000);
    };
    ws.onerror = () => {};
    // Detect silent disconnects.
    clearInterval((this as any).pingTimer);
    (this as any).pingTimer = setInterval(() => this.send({ t: 'ping' }), 25_000);
  }

  private send(m: TvMessage) {
    if (this.ws?.readyState === 1) this.ws.send(JSON.stringify(m));
    else if (m.t === 'log' || m.t === 'action') this.outbox.push(m);
  }

  private async onMessage(m: ServerMessage) {
    switch (m.t) {
      case 'welcome':
        if (this.outdated(m.runtime)) return;
        this.approved = m.approved;
        if (m.token) store(`fl.token.${this.baseUrl}`, m.token);
        if (m.locale) this.setLocale(m.locale);
        this.emit();
        return;
      case 'screen':
        this.doc = m.doc;
        this.bundles = m.bundles;
        this.setLocale(m.doc.locale);
        this.resetIdle();
        await this.loadBundles();
        this.persist();
        this.emit();
        return;
      case 'channel': {
        const key = `${m.module}\0${m.channel}`;
        this.channels.set(key, m.data);
        this.channelSubs.get(key)?.forEach((cb) => cb(m.data));
        this.persistSoon();
        return;
      }
      case 'action:result': {
        const p = this.pending.get(m.reqId);
        if (!p) return;
        this.pending.delete(m.reqId);
        if (m.ok) p.res(m.data);
        else p.rej(new Error(m.error));
        return;
      }
      case 'lan:request':
        return this.edgeLan(m);
      case 'tv:request':
        return this.edgeTv(m);
      case 'command':
        if (m.action === 'sleep') this.sleep();
        else if (m.action === 'wake') this.wake();
        else if (m.action === 'reload') location.reload();
        else if (m.action === 'toast') this.toast(String(m.data ?? ''));
        this.commandSubs.get(m.action)?.forEach((cb) => cb(m.data));
        return;
      case 'reload':
        location.reload();
        return;
    }
  }

  /** If the server's runtime changed (a new build was deployed), reload the page once. */
  private outdated(serverHash?: string) {
    const mine = new URL(import.meta.url).searchParams.get('v');
    if (!serverHash || !mine || serverHash === mine) return false;
    // Loop guard: try only once per target version.
    if (store('fl.reloadedFor') === serverHash) return false;
    store('fl.reloadedFor', serverHash);
    this.log('info', `runtime updated (${mine} → ${serverHash}), reloading`);
    setTimeout(() => location.reload(), 300);
    return true;
  }

  private persistTimer?: ReturnType<typeof setTimeout>;
  private persistSoon() {
    clearTimeout(this.persistTimer);
    this.persistTimer = setTimeout(() => this.persist(), 2000);
  }
  private persist() {
    if (PREVIEW) return;
    const channels: Record<string, unknown> = {};
    this.channels.forEach((v, k) => (channels[k] = v));
    store(this.cacheKey(), JSON.stringify({ doc: this.doc, bundles: this.bundles, channels }));
  }

  // ---------- widget bundles ----------
  private async loadBundles() {
    await Promise.all(
      Object.entries(this.bundles).map(([id, b]) => {
        const key = b.js;
        if (!this.loadingBundles.has(key)) {
          this.loadingBundles.set(
            key,
            (async () => {
              if (b.css) this.injectCss(id, new URL(b.css.slice(1), this.baseUrl).href);
              try {
                const mod = await import(/* @vite-ignore */ new URL(b.js.slice(1), this.baseUrl).href);
                this.widgets.set(id, mod.default || {});
              } catch (e) {
                this.log('error', `failed to load the ${id} bundle: ${(e as Error).message}`, 'runtime');
                this.loadingBundles.delete(key);
              }
            })(),
          );
        }
        return this.loadingBundles.get(key);
      }),
    );
    this.emit();
  }

  private injectCss(id: string, href: string) {
    const existing = document.querySelector(`link[data-module="${id}"]`) as HTMLLinkElement | null;
    if (existing?.href === href) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.dataset.module = id;
    link.onload = () => existing?.remove();
    document.head.appendChild(link);
  }

  // ---------- Edge ----------
  private async edgeLan(m: Extract<ServerMessage, { t: 'lan:request' }>) {
    try {
      const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
      const timer = setTimeout(() => ctrl?.abort(), m.timeoutMs);
      const r = await fetch(m.url, { method: m.method, headers: m.headers, body: m.body, signal: ctrl?.signal });
      clearTimeout(timer);
      const headers: Record<string, string> = {};
      r.headers.forEach((v, k) => (headers[k] = v));
      this.send({ t: 'lan:response', reqId: m.reqId, ok: true, status: r.status, headers, body: await r.text() });
    } catch (e) {
      this.send({ t: 'lan:response', reqId: m.reqId, ok: false, error: `${m.url}: ${(e as Error).message}` });
    }
  }

  private async edgeTv(m: Extract<ServerMessage, { t: 'tv:request' }>) {
    try {
      if (m.api === 'frame.openApp') {
        const [appId, opts] = m.args as [string, OpenAppOptions | undefined];
        await this.openApp(String(appId), opts);
        return this.send({ t: 'tv:response', reqId: m.reqId, ok: true, result: { ok: true, preview: !IS_TV } });
      }
      if (m.api.startsWith('frame.')) return this.send({ t: 'tv:response', reqId: m.reqId, ok: true, result: await this.remote.call(m.api, m.args) });
      if (!IS_TV) throw new Error('Not a TV (preview)');
      if (m.api === 'tizen.application.launch') this.remote.noteLaunch(m.args[0]);
      if (/^tizen\.application\.launch/.test(m.api)) releaseAudio();
      this.send({ t: 'tv:response', reqId: m.reqId, ok: true, result: await callTvApi(m.api, m.args) });
    } catch (e) {
      this.send({ t: 'tv:response', reqId: m.reqId, ok: false, error: (e as Error).message });
    }
  }

  // ---------- RuntimeApi ----------
  action(widgetId: string, name: string, input?: unknown) {
    const reqId = `a${++this.seq}`;
    return new Promise<unknown>((res, rej) => {
      this.pending.set(reqId, { res, rej });
      this.send({ t: 'action', reqId, widgetId, action: name, input });
      setTimeout(() => {
        if (this.pending.delete(reqId)) rej(new Error(this.t.noResponse));
      }, 25_000);
    });
  }

  getChannel(module: string, channel: string) {
    return this.channels.get(`${module}\0${channel}`);
  }

  subscribe(module: string, channel: string, cb: (v: unknown) => void) {
    return sub(this.channelSubs, `${module}\0${channel}`, cb);
  }

  getShared(module: string, key: string) {
    return this.shared.get(`${module}\0${key}`);
  }

  setShared(module: string, key: string, value: unknown) {
    const k = `${module}\0${key}`;
    this.shared.set(k, value);
    this.sharedSubs.get(k)?.forEach((cb) => cb(value));
  }

  onShared(module: string, key: string, cb: (v: unknown) => void) {
    return sub(this.sharedSubs, `${module}\0${key}`, cb);
  }

  mode() {
    return this.modeValue;
  }

  onMode(cb: (m: Mode) => void) {
    this.modeSubs.add(cb);
    return () => this.modeSubs.delete(cb);
  }

  locale() {
    return this.localeValue;
  }

  onLocale(cb: (l: Locale) => void) {
    this.localeSubs.add(cb);
    return () => this.localeSubs.delete(cb);
  }

  /** Runtime strings in the current language. */
  get t() {
    return messages.get(this.localeValue);
  }

  private setLocale(l: Locale | undefined) {
    const next = isLocale(l) ? l : DEFAULT_LOCALE;
    document.documentElement.lang = next;
    if (next === this.localeValue) return;
    this.localeValue = next;
    if (!PREVIEW) store('fl.locale', next);
    this.localeSubs.forEach((cb) => cb(next));
    this.emit();
  }

  private setMode(m: Mode) {
    if (this.modeValue === m) return;
    this.modeValue = m;
    document.body.classList.toggle('is-idle', m === 'idle');
    this.modeSubs.forEach((cb) => cb(m));
    if (!PREVIEW) store('fl.mode', m);
  }

  sleep = () => {
    clearTimeout(this.idleTimer);
    this.setMode('idle');
  };

  wake = () => {
    this.setMode('active');
    this.resetIdle();
  };

  renderZone(name: string) {
    return this.zoneRenderer(name);
  }

  holdAwake = () => {
    this.awakeHolds++;
    clearTimeout(this.idleTimer);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.awakeHolds--;
      this.resetIdle();
    };
  };

  resetIdle() {
    clearTimeout(this.idleTimer);
    if (FORCED_MODE || this.modeValue !== 'active' || this.awakeHolds > 0) return;
    const s = this.doc?.ambient.idleSeconds ?? 30;
    this.idleTimer = setTimeout(this.sleep, s * 1000);
  }

  /** Initial mode: ?mode in the preview, gallery on the TV. */
  initMode() {
    this.setMode(FORCED_MODE || 'idle');
    document.body.classList.toggle('is-idle', this.modeValue === 'idle');
  }

  tv = (api: string, ...args: unknown[]) => {
    if (api === 'tizen.application.launch') this.remote.noteLaunch(args[0]);
    if (IS_TV && /^tizen\.application\.launch/.test(api)) releaseAudio();
    if (!IS_TV) {
      if (api === 'tizen.application.launch') {
        this.toast(this.t.previewLaunch(String(args[0])));
        return Promise.resolve(undefined);
      }
      return Promise.reject(new Error('No TV API in the preview'));
    }
    return callTvApi(api, args);
  };

  onCommand(name: string, cb: (d?: unknown) => void) {
    return sub(this.commandSubs, name, cb);
  }

  onBack(cb: () => boolean) {
    this.backHandlers.push(cb);
    return () => {
      this.backHandlers = this.backHandlers.filter((h) => h !== cb);
    };
  }

  /** Back key: first the widgets' open layers, then switch to gallery mode. */
  back() {
    for (let i = this.backHandlers.length - 1; i >= 0; i--) if (this.backHandlers[i]()) return;
    this.sleep();
  }

  /** Bring the launcher to the front (the page isn't reloaded; config.xml reload="disable"). */
  bringToFront = () => {
    if (!IS_TV) return;
    const self = tizen.application.getCurrentApplication().appInfo.id;
    const fallback = () => tizen.application.launch(self, () => {}, (e: Error) => this.log('warn', `bring to front: ${e.message}`, 'runtime'));
    try {
      tizen.application.launchAppControl(new tizen.ApplicationControl('http://frame-launcher.local/operation/resume'), self, () => {}, fallback);
    } catch {
      fallback();
    }
  };

  private returnWatcher?: ReturnType<typeof setInterval>;

  openApp = async (appId: string, opts: OpenAppOptions = {}) => {
    this.remote.noteLaunch(appId);
    if (!IS_TV) {
      this.toast(this.t.previewLaunch(`${appId}${opts.dial?.body ? ` (${opts.dial.body})` : ''}`));
      return;
    }
    releaseAudio();
    if (opts.dial) {
      // The response may be blocked by CORS; the request still goes out (no-cors).
      await fetch(`http://127.0.0.1:8080/ws/apps/${encodeURIComponent(opts.dial.name)}`, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: opts.dial.body || '',
      });
    } else if (opts.control) await callTvApi('tizen.application.launchAppControl', [toAppControl(opts.control), appId]);
    else await callTvApi('tizen.application.launch', [appId]);
    const storeId = opts.storeId || KNOWN_STORE_IDS[appId];
    if (storeId) this.watchReturn(storeId);
  };

  /** The opened app shows up first; once it leaves the screen (Back/Home), bring the launcher to the front. */
  private watchReturn(storeId: string) {
    clearInterval(this.returnWatcher);
    let shown = false;
    let hidden = 0;
    let ticks = 0;
    const stop = () => clearInterval(this.returnWatcher);
    this.returnWatcher = setInterval(async () => {
      ticks++;
      if (document.visibilityState === 'visible' && ticks > 3) return stop();
      let visible = false;
      try {
        visible = (await fetch(TV_APPS_API + storeId, { cache: 'no-store' }).then((r) => r.json())).visible;
      } catch (e) {
        this.log('warn', `return watcher error: ${(e as Error).message}`);
        return stop();
      }
      if (visible) {
        shown = true;
        hidden = 0;
      } else if (shown && ++hidden >= 2) {
        stop();
        this.bringToFront();
      } else if (!shown && ticks > 30) stop();
    }, 700);
  }

  toast = (message: string) => {
    this.toastText = message;
    this.emit();
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => {
      this.toastText = '';
      this.emit();
    }, 4000);
  };

  playAudio = playAudio;
  preloadAudio = preloadAudio;

  log = (level: 'info' | 'warn' | 'error', message: string, source = 'runtime') => {
    this.send({ t: 'log', level, source, message: String(message).slice(0, 4000) });
    if (level === 'error') console.error(`[${source}]`, message);
  };

  /** Asset URL: build-hashed (immutable, cacheable); a fixed URL for modules without a bundle. */
  assetUrl(module: string, path: string) {
    const rel = path.replace(/^\/+/, '');
    const js = this.bundles[module]?.js;
    if (js) return new URL(js.replace(/client\.js$/, `assets/${rel}`).slice(1), this.baseUrl).href;
    return `${this.baseUrl}m/${module}/assets/${rel}`;
  }

}

/** Turn a plain object into a tizen.ApplicationControl (leave it alone if it already is one). */
function toAppControl(c: any) {
  if (typeof tizen === 'undefined' || c instanceof tizen.ApplicationControl) return c;
  const data = (c.data || []).map((d: { key: string; value: string[] }) => new tizen.ApplicationControlData(d.key, d.value));
  return new tizen.ApplicationControl(c.operation || 'http://tizen.org/appcontrol/operation/view', c.uri ?? null, c.mime ?? null, null, data);
}

function sub<T>(map: Map<string, Set<T>>, key: string, cb: T) {
  let set = map.get(key);
  if (!set) map.set(key, (set = new Set()));
  set.add(cb);
  return () => {
    set!.delete(cb);
  };
}
